import React from "react";
import { type NextPage } from "next";
import { Box, Flex, SimpleGrid } from "@chakra-ui/react";
import { Page } from "components/page";
import { Section, sectionSpacingY } from "components/section";
import { StackedLogos } from "components/logos";
import { CenteredQuote } from "components/quotes";
import { useDemoLinkWithParameters } from "hooks/use-demo-link";
import { getLandingPageProps, getPageMetaImage, type LandingPageProps } from "utils";
import { Button } from "ui/button";
import { Diptych } from "ui/diptych";
import Image from "next/image";
import { GlobeIcon, LightningIcon, PersonWithCheckIcon, SettingsIcon } from "ui/icons";
import { TextColumn } from "ui/text";
import { LegacyMediaHero } from "components/hero/legacy-media-hero";
import heroImage from "../../../public/assets/platform/personalization-api/hero.svg";
import fullWidthImage from "../../../public/assets/platform/personalization-api/full-width.png";
import fullWidthMobileImage from "../../../public/assets/platform/personalization-api/full-width-mobile.svg";
import pair1Image from "../../../public/assets/platform/personalization-api/pair-1.png";
import pair2Image from "../../../public/assets/platform/personalization-api/pair-2.png";
import pair3Image from "../../../public/assets/platform/personalization-api/pair-3.png";

const PersonalizationAPI: NextPage<LandingPageProps> = ({ page, announcementBanner, defaultLogos }) => {
  const demoLinkWithParameters = useDemoLinkWithParameters();

  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      bgColor="new.blue.150"
      footerOptions={{ variant: "blue" }}
    >
      <LegacyMediaHero
        variant="blue"
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        imageSrc={heroImage}
        imageAlt="A diagram showing connections to email marketing with HubSpot, in-app communications with Intercom, and Braze as a customer engagement platform."
      />

      <StackedLogos my="0" pt="small" customers={defaultLogos} />

      <Section>
        <Diptych
          direction="left"
          mediaRatio={{ base: 1 / 1.5, sm: 2 / 1 }}
          media={
            <>
              <Box opacity={{ base: 0, sm: 1 }}>
                <Image
                  sizes="100%"
                  src={fullWidthImage}
                  alt="Data moving through a warehouse, to Hightouch Personalization API, to any application."
                />
              </Box>
              <Box aria-hidden opacity={{ base: 1, sm: 0 }}>
                <Image sizes="100%" src={fullWidthMobileImage} alt="" style={{ maxHeight: "100%" }} />
              </Box>
            </>
          }
          title="Write once, use anywhere"
          description="Your warehouse is already loaded with customer, product, and transaction data.  Call on it in real-time with a GET request to dynamically personalize any user experience."
          actions={
            <Button hasRightChevron href="/blog/the-future-of-the-warehouse">
              Read the blog
            </Button>
          }
        />
      </Section>

      <Section>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} width="100%" gap={{ base: "biggest", md: "small" }}>
          <TextColumn
            iconChip={{ variant: "green", icon: LightningIcon }}
            title="Lightning fast"
            description="Deliver the right message at the right time with sub-30 millisecond latency."
          />

          <TextColumn
            iconChip={{ variant: "yellow", icon: SettingsIcon }}
            title="Flexible customization"
            description="Use any data type — not just user profiles — to personalize customer experiences."
          />

          <TextColumn
            iconChip={{ variant: "blue", icon: PersonWithCheckIcon }}
            title="Personalize anything"
            description="Populate any endpoint with relevant and up-to-date data."
          />

          <TextColumn
            iconChip={{ variant: "violet", icon: GlobeIcon }}
            title="Fully managed"
            description="Avoid internal politics and get started fast with fully-managed, globally distributed service."
          />
        </SimpleGrid>
      </Section>

      <Section>
        <Flex flexDir="column" gap={{ base: "huge", md: "hugest" }}>
          <Diptych
            direction="right"
            mediaRatio={577 / 473}
            media={
              <Box p="small">
                <Image src={pair1Image} alt="" />
              </Box>
            }
            title="Power personalized marketing campaigns"
            description="Enrich marketing touchpoints — including emails or SMS messages — with dynamic data points like product recommendations."
            actions={
              <Button hasRightChevron href="/blog/personalization-api">
                Read the blog
              </Button>
            }
          />

          <Diptych
            direction="left"
            mediaRatio={49 / 31}
            media={
              <Box p="small">
                <Image src={pair2Image} alt="" />
              </Box>
            }
            title="Tailor in-app experiences on the fly"
            description="Drive conversions by personalizing your digital experiences based on what matters to the user. Whether that’s a customized search result, featured product recommendation, or bespoke UI."
            actions={
              <Button hasRightChevron href={demoLinkWithParameters}>
                Request a demo
              </Button>
            }
          />

          <Diptych
            direction="right"
            mediaRatio={522 / 407}
            media={
              <Box p="small">
                <Image src={pair3Image} alt="" />
              </Box>
            }
            title="Run real-time product experiments"
            description="Use up-to-date customer information to run experiments so you can continuously optimize operations and your digital customer experience."
            actions={
              <Button hasRightChevron href="/blog/personalization-api">
                Read the blog
              </Button>
            }
          />
        </Flex>
      </Section>

      <Section my={0} pb={sectionSpacingY}>
        <CenteredQuote
          author={{
            name: "Austin Hay",
            job: "Head of Marketing Technology",
          }}
          company={{
            name: "Ramp",
            logo: "https://cdn.sanity.io/images/pwmfmi47/production/acaf90882a1ded8a3796cc3972219355a42d009c-270x72.svg",
          }}
          quoteText="Traditional CDPs fail to deliver personalization at most organizations because the way they treat data doesn’t allow for experimentation and agility... Hightouch’s Personalization API drastically simplifies personalization by leveraging the data warehouse– giving teams the ability to experiment quickly with more complex architectures."
          cta={{ href: "/customers/ramp", text: "Read customer story" }}
        />
      </Section>
    </Page>
  );
};

export default PersonalizationAPI;

export const getStaticProps = getLandingPageProps("/platform/personalization-api", {
  loadDefaultLogos: true,
});
